import React, { useState, useEffect, useCallback } from "react";
import {
  FormControl,
  Grid,
  Typography,
  Tab,
  Box,
  Stack,
  IconButton,
} from "@mui/material";
import Api from "../../../services/Api";
import AccountPicker from "./AccountPicker";
import { AccountItem } from "../../../services/grace";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAlertDispatch } from "../../../context/AlertContext/AlertContext";

import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";

//redux
import { useAppDispatch, useAppSelector } from "../../../util/hooks";
import { setOptionsBasic, setOptionsCheckout, setOptionsAddress, setOptionsContainer,
  setOptionsFile, setOptionsTape, setSearchBasicFormText, setSearchAddressFormText, setSearchCheckoutFormText, setSearchContainerFormText, setSearchTapeFormText, setSearchFileFormText, 
  setSelectedValuesBasicForm, setSelectedValuesAddressForm, setSelectedValuesCheckoutForm, 
  setSelectedValuesContainerForm, setSelectedValuesFileForm, setSelectedValuesTapeForm } from "../../../features/search/searchSlice";

export interface AccountPickerGroupValue {
  level1: string;
  level2: string;
  level3: string;
}

export interface AccountPickerGroupProps {
  label: string;
  labelVariant?: "subtitle1" | "subtitle2";
  value: AccountPickerGroupValue;
  error?: boolean;
  helperText?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  onChange: (value: AccountPickerGroupValue) => void;
  isHorizontal?: boolean;
  accountSearchText?:string | null;
  handleAccountTextChange?: any;
  type: "basic" | "checkout" | "address" | "container" | "file" | "tape";
}

export interface AccountPickerValue {
  id: string;
  label: string;
}

export default function AccountPickerGroup(
  props: AccountPickerGroupProps
): JSX.Element {
  const {
    labelVariant = "subtitle2",
    value,
    required = false,
    disabled = false,
    onChange,
    isHorizontal = true,
    type,
    accountSearchText,
    handleAccountTextChange
  } = props;

  const myRef = React.useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();
  const alertDispatch = useAlertDispatch();
  const [tab, setTab] = React.useState("1");
  const [lock, setLock] = React.useState(false);
  const [valueText1, setValueText1] = useState(accountSearchText??"");
  const optionsBasic = useAppSelector((state) => state?.search.optionsBasic);
  const optionsCheckout = useAppSelector((state) => state?.search.optionsCheckout);
  const optionsAddress = useAppSelector((state) => state?.search.optionsAddress);
  const optionsContainer = useAppSelector((state) => state?.search.optionsContainer);
  const optionsFile = useAppSelector((state) => state?.search.optionsFile);
  const optionsTape = useAppSelector((state) => state?.search.optionsTape);
  const searchBasicFormText = useAppSelector((state) => state?.search.searchBasicFormText);
  const searchAddressFormText = useAppSelector((state) => state?.search.searchAddressFormText);
  const searchCheckoutFormText = useAppSelector((state) => state?.search.searchCheckoutFormText);
  const searchFileFormText = useAppSelector((state) => state?.search.searchFileFormText);
  const searchTapeFormText = useAppSelector((state) => state?.search.searchTapeFormText);
  const searchContainerFormText = useAppSelector((state) => state?.search.searchContainerFormText);
  const selectedValuesBasicForm = useAppSelector((state) => state?.search.selectedValuesBasicForm);
  const selectedValuesCheckoutForm = useAppSelector((state) => state?.search.selectedValuesCheckoutForm);
  const selectedValuesAddressForm = useAppSelector((state) => state?.search.selectedValuesAddressForm);
  const selectedValuesContainerForm = useAppSelector((state) => state?.search.selectedValuesContainerForm);
  const selectedValuesFileForm = useAppSelector((state) => state?.search.selectedValuesFileForm);
  const selectedValuesTapeForm = useAppSelector((state) => state?.search.selectedValuesTapeForm);

  const filterAccountTypeOptions = useCallback(
    (
      type: "basic" | "checkout" | "address" | "container" | "file" | "tape"
    ): AccountPickerValue[] | null => {
      let result;
      switch (type) {
        case "basic":
          result = optionsBasic;
          break;
        case "checkout":
          result = optionsCheckout;
          break;
        case "address":
          result = optionsAddress;
          break;
        case "container":
          result = optionsContainer;
          break;
        case "file":
          result = optionsFile;
          break;
        case "tape":
          result = optionsTape;
          break;
      }
      return result;
    },
    [
      optionsBasic,
      optionsAddress,
      optionsCheckout,
      optionsContainer,
      optionsFile,
      optionsTape,
    ]
  );

  const filterAccountTypeSearchText = useCallback(
    (
      type: "basic" | "checkout" | "address" | "container" | "file" | "tape"
    ): string | null => {
      let result;
      switch (type) {
        case "basic":
          result = searchBasicFormText;
          break;
        case "checkout":
          result = searchCheckoutFormText;
          break;
        case "address":
          result = searchAddressFormText;
          break;
        case "container":
          result = searchContainerFormText;
          break;
        case "file":
          result = searchFileFormText;
          break;
        case "tape":
          result = searchTapeFormText;
          break;
      }
      return result;
    },
    [
      searchAddressFormText,
      searchBasicFormText,
      searchCheckoutFormText,
      searchContainerFormText,
      searchFileFormText,
      searchTapeFormText,
    ]
  );

  const filterAccountTypeSelectedValues = useCallback(
    (
      type: "basic" | "checkout" | "address" | "container" | "file" | "tape"
    ): AccountPickerGroupValue | null => {
      let result;
      switch (type) {
        case "basic":
          result = selectedValuesBasicForm;
          break;
        case "checkout":
          result = selectedValuesCheckoutForm;
          break;
        case "address":
          result = selectedValuesAddressForm;
          break;
        case "container":
          result = selectedValuesContainerForm;
          break;
        case "file":
          result = selectedValuesFileForm;
          break;
        case "tape":
          result = selectedValuesTapeForm;
          break;
      }
      return result;
    },
    [
      selectedValuesAddressForm,
      selectedValuesBasicForm,
      selectedValuesCheckoutForm,
      selectedValuesContainerForm,
      selectedValuesFileForm,
      selectedValuesTapeForm,
    ]
  );

  const filterAccountTypeDispatchOptions = useCallback(
    (
      type: "basic" | "checkout" | "address" | "container" | "file" | "tape",
      value: AccountPickerValue[] | null
    ) => {
      switch (type) {
        case "basic":
          dispatch(setOptionsBasic(value));
          break;
        case "checkout":
          dispatch(setOptionsCheckout(value));
          break;
        case "address":
          dispatch(setOptionsAddress(value));
          break;
        case "container":
          dispatch(setOptionsContainer(value));
          break;
        case "file":
          dispatch(setOptionsFile(value));
          break;
        case "tape":
          dispatch(setOptionsTape(value));
          break;
      }
    },
    [dispatch]
  );

  const filterAccountTypeDispatchSearchText = useCallback(
    (
      type: "basic" | "checkout" | "address" | "container" | "file" | "tape",
      value: string | null
    ) => {
      switch (type) {
        case "basic":
          dispatch(setSearchBasicFormText(value));
          break;
        case "checkout":
          dispatch(setSearchCheckoutFormText(value));
          break;
        case "address":
          dispatch(setSearchAddressFormText(value));
          break;
        case "container":
          dispatch(setSearchContainerFormText(value));
          break;
        case "file":
          dispatch(setSearchFileFormText(value));
          break;
        case "tape":
          dispatch(setSearchTapeFormText(value));
          break;
      }
    },
    [dispatch]
  );

  const filterAccountTypeDispatchSelectedValues = useCallback(
    (
      type: "basic" | "checkout" | "address" | "container" | "file" | "tape",
      value: AccountPickerGroupValue | null
    ) => {
      switch (type) {
        case "basic":
          dispatch(setSelectedValuesBasicForm(value));
          break;
        case "checkout":
          dispatch(setSelectedValuesCheckoutForm(value));
          break;
        case "address":
          dispatch(setSelectedValuesAddressForm(value));
          break;
        case "container":
          dispatch(setSelectedValuesContainerForm(value));
          break;
        case "file":
          dispatch(setSelectedValuesFileForm(value));
          break;
        case "tape":
          dispatch(setSelectedValuesTapeForm(value));
          break;
      }
    },
    [dispatch]
  );

  const handleLevel1Change = (item: string) => {
    onChange({
      level1: item,
      level2: "",
      level3: "",
    });
  };

  const handleLevel2Change = (item: string) => {
    onChange({
      level1: value.level1,
      level2: item,
      level3: "",
    });
  };

  const handleLevel3Change = (item: string) => {
    onChange({
      level1: value.level1,
      level2: value.level2,
      level3: item,
    });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTab(newValue);
  };

  const handleChange = useCallback(
    (e: AccountPickerValue) => {
      const selectedids = filterAccountTypeOptions(type)?.find(
        (v) => v.id === e.id
      )?.id;
      const mySelectedValues = {
        level1: selectedids?.split(";")[0] ?? "",
        level2: selectedids?.split(";")[1] ?? "",
        level3: selectedids?.split(";")[2] ?? "",
      };

      if (
        (mySelectedValues?.level1 !== "" &&
          filterAccountTypeSelectedValues(type)?.level1 !==
            selectedids?.split(";")[0]) ||
        (mySelectedValues?.level2 !== "" &&
          filterAccountTypeSelectedValues(type)?.level2 !==
            selectedids?.split(";")[1]) ||
        (mySelectedValues?.level3 !== "" &&
          filterAccountTypeSelectedValues(type)?.level3 !==
            selectedids?.split(";")[2])
      )
        setTab("1");

      filterAccountTypeDispatchSelectedValues(type, mySelectedValues);
    },
    [
      filterAccountTypeOptions,
      type,
      filterAccountTypeDispatchSelectedValues,
      filterAccountTypeSelectedValues,
    ]
  );

  const btnSearchClick = useCallback(async () => {
      const myText = valueText1;
    try {
      if (myText) {
        filterAccountTypeDispatchSearchText(type, myText);
        const api = new Api("");
        const result = await api.getSearchAccountData(myText);
        if (result) {
          setLock(true);
          filterAccountTypeDispatchOptions(type, generateAccountList(result));
        }
        myRef.current && myRef.current.focus();
      } else {
        alertDispatch({
          type: "add",
          payload: {
            type: "general",
            message: "Please input some valid text to search.",
          },
        });
      }
    } catch (err: any) {
      alertDispatch({
        type: "add",
        payload: {
          type: "general",
          message:
            "Error: " +
            (Object.keys(err).length === 0 ? err : `'${myText}' text was not found from account list of GRACE.`),
        },
      });
    }
  }, [alertDispatch, valueText1, filterAccountTypeDispatchSearchText, type, filterAccountTypeDispatchOptions]);

  const btnClearClick = () => {
    setLock(false);
    setValueText1("");
    filterAccountTypeDispatchOptions(type, null);
    filterAccountTypeDispatchSearchText(type, null);
    filterAccountTypeDispatchSelectedValues(type, null);
    if (handleAccountTextChange) handleAccountTextChange("");
  };

  const generateAccountList = (arr:[]): AccountPickerValue[] => {
   
    const result: AccountPickerValue[] = [];

    arr?.forEach((items: AccountPickerValue) => {
      const ids: string[] = [];
      const labels: string[] = [];
      Object.values(items).forEach((item: AccountItem) => {
        if (Object.keys(item).length > 0) {
          ids.push(item.id);
          labels.push(
            item.name === undefined ? item.code : `${item.code} ${item.name}`
          );
        }
      });
      result.push({ id: ids.join(";"), label: labels.join(";") });
    });
    return result;
  };
 
  const ids = (): string => {
    const result: string[] = [];
    const selectedValues = filterAccountTypeSelectedValues(type);

    if (selectedValues) {
      Object.values(selectedValues).forEach(
        (item: string | null | undefined) => {
          if (item) {
            result.push(item);
          }
        }
      );
    }

    return result.join(";");
  };

  useEffect(() => {
    if (handleAccountTextChange) {
      handleAccountTextChange;
    }
    const searchText: string | null = filterAccountTypeSearchText(type);
    setValueText1(searchText ?? "");

    if (accountSearchText === "" && ["basic"].includes(type)) {
      setLock(false);
    }
    if (
      accountSearchText &&
      ["basic"].includes(type) &&
      accountSearchText.match(/^[A-Za-z]*$/)
    ) {
      setValueText1(accountSearchText ?? "");
    }
  }, [
    lock,
    type,
    accountSearchText,
    handleAccountTextChange,
    filterAccountTypeSearchText,
  ]);

  useEffect(() => {
    if (value.level1 === "" && filterAccountTypeSelectedValues(type)?.level1 === "") {
      setValueText1("");
      filterAccountTypeDispatchOptions(type, null);
      filterAccountTypeDispatchSearchText(type, null);
      filterAccountTypeDispatchSelectedValues(type, null);
    }
  }, [
    value,
    filterAccountTypeSelectedValues,
    filterAccountTypeDispatchOptions,
    filterAccountTypeDispatchSearchText,
    filterAccountTypeDispatchSelectedValues,
    type,
  ]);

  return (
    <Grid container>
      <Grid item xs={12} sx={{ textAlign: "left", marginBottom: 1 }}>
        <TabContext value={tab}>
          <TabList
            onChange={handleTabChange}
            TabIndicatorProps={{
              style: {
                background: "#db291d",
              },
            }}
          >
            <Tab
              label={
                <Typography
                  variant={labelVariant}
                  sx={{
                    marginLeft: "-5x",
                    marginRight: "-10px",
                    fontWeight: 500,
                    color: "black",
                    textTransform: "capitalize",
                  }}
                >
                  Account Picker &ensp; |
                </Typography>
              }
              value="1"
            />
            <Tab
              label={
                <Typography
                  variant={labelVariant}
                  sx={{
                    marginLeft: "-20px",
                    marginRight: "-10px",
                    fontWeight: 500,
                    color: "black",
                    textTransform: "capitalize",
                  }}
                >
                  &ensp; Account Lookup
                </Typography>
              }
              value="2"
            />
          </TabList>
          <TabPanel value="1" sx={{ marginLeft: "-10px", marginTop: "-10px" }}>
            <Grid item xs={12} sx={{ marginBottom: 1 }}>
              <Stack
                spacing={1}
                justifyContent="flex-start"
                direction={isHorizontal ? "row" : "column"}
              >
                <Grid item xs={7}>
                  <FormControl sx={{ marginRight: 1, width: "99%" }}>
                    <AccountPicker
                      name="level1"
                      type={type}
                      required={required}
                      label="Level 1"
                      value={value.level1}
                      disabled={disabled}
                      onChange={handleLevel1Change}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={7}>
                  <FormControl sx={{ marginRight: 1, width: "99%" }}>
                    <AccountPicker
                      name="level2"
                      type={type}
                      label="Level 2"
                      parentValue={value.level1}
                      value={value.level2}
                      disabled={disabled || value.level1 === ""}
                      onChange={handleLevel2Change}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={7}>
                  <FormControl sx={{ width: "99%" }}>
                    <AccountPicker
                      name="level3"
                      type={type}
                      label="Level 3"
                      parentValue={value.level2}
                      value={value.level3}
                      disabled={disabled || value.level2 === ""}
                      onChange={handleLevel3Change}
                    />
                  </FormControl>
                </Grid>
              </Stack>
            </Grid>
          </TabPanel>
          <TabPanel
            value="2"
            sx={{
              marginLeft: "-10px",
              marginTop: "-10px",
            }}
          >
            <Grid item xs={12} sx={{ marginBottom: 1 }}>
              <Box
                sx={{
                  maxWidth: "100%",
                  height: !isHorizontal ? "11.5em" : "100%",
                }}
              >
                <Autocomplete
                  id="accountSearchText"
                  freeSolo
                  disableClearable
                  filterOptions={(options) => options}
                  options={filterAccountTypeOptions(type) ?? []}
                  value={`${valueText1}`}
                  sx={{ width: "100%" }}
                  onInputChange={(e, newInputValue: string) => {
                    if (newInputValue.match(/^[A-Za-z]*$/) || lock === false) {
                      setValueText1(newInputValue);
                    }
                    if (handleAccountTextChange)
                      handleAccountTextChange(newInputValue);
                  }}
                  onKeyDown={(e) => {
                    e.keyCode == 13 ? e.preventDefault() : lock ? e.preventDefault() : "";
                  }}
                  isOptionEqualToValue={(
                    option: AccountPickerValue,
                    value: AccountPickerValue
                  ) => option.id === value.id}
                  onChange={(e, newInputValue: any) =>
                    handleChange(newInputValue)
                  }
                  // value={filterAccountTypeOptions(type)?.find((v) => v.id === ids()) ? "op":  ""}
                  renderOption={(props, options) => (
                    <ul
                      key={options.id}
                      style={{ listStyleType: "none", padding: 0 }}
                    >
                      <li {...props}>
                        <div className="container-flexbox">
                          <span style={{ width: "400px" }}>
                            {options.label.split(";")[0]}
                          </span>
                          <span style={{ width: "400px" }}>
                            {options.label.split(";")[1]}
                          </span>
                          <span style={{ width: "400px" }}>
                            {options.label.split(";")[2]}
                          </span>
                        </div>
                      </li>
                    </ul>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder={"Search Accounts Level 1,2 & 3"}
                      inputRef={myRef}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {filterAccountTypeSearchText(type) === null ? (
                              <IconButton
                                sx={{ marginRight: 1 }}
                                onClick={btnSearchClick}
                                edge="end"
                                size="small"
                              >
                                <FontAwesomeIcon
                                  size="sm"
                                  icon={["fas", "magnifying-glass"]}
                                />
                              </IconButton>
                            ) : (
                              <IconButton
                                sx={{ marginRight: 1 }}
                                onClick={btnClearClick}
                                edge="end"
                                size="small"
                              >
                                <FontAwesomeIcon
                                  size="sm"
                                  icon={["fas", "xmark"]}
                                />
                              </IconButton>
                            )}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            </Grid>
          </TabPanel>
        </TabContext>
      </Grid>
    </Grid>
  );
}
